<script lang="ts" setup>
import { ref, watch, onBeforeUnmount, toRaw, computed, useAttrs } from 'vue'

defineOptions({
  inheritAttrs: false
})

const props = defineProps<{
  src?: string | File | Blob
  collectionId?: string
  id?: string
  alt?: string
  thumb?: string // Pocketbase generated thumbnail
  provider?: string
  fetchPriority?: 'high' | 'low' | 'auto'
}>()

// 🚀 Access parent attributes to pass them down cleanly
const attrs = useAttrs()

const isImageReady = ref(false)
const handleLoad = () => {
  isImageReady.value = true
}

// Memory leak protection
let currentObjectUrl: string | null = null
const cleanupObjectUrl = () => {
  if (currentObjectUrl) {
    URL.revokeObjectURL(currentObjectUrl)
    currentObjectUrl = null
  }
}
onBeforeUnmount(cleanupObjectUrl)

const blobUrl = ref<string | null>(null)

watch(
  () => props.src,
  (newSrc) => {
    cleanupObjectUrl()
    const rawSrc = toRaw(newSrc)

    if (rawSrc instanceof File || rawSrc instanceof Blob) {
      currentObjectUrl = URL.createObjectURL(rawSrc)
      blobUrl.value = currentObjectUrl
    } else {
      blobUrl.value = null
    }
  },
  { immediate: true }
)

// 🚀 Optimization: Compute logic once rather than in the template
const isImageString = computed(() => typeof props.src === 'string')
const isStandardImageFormat = computed(
  () =>
    isImageString.value
    && /\.(jpg|jpeg|png|gif|webp)$/i.test(props.src as string)
)
const hasValidPbData = computed(
  () => !!(props.collectionId && props.id && isImageString.value)
)

const finalSrc = computed(() => {
  if (blobUrl.value) return blobUrl.value
  if (!hasValidPbData.value) return ''

  const direct = !isStandardImageFormat.value
  return getImagePath(
    {
      collectionId: props.collectionId as string,
      id: props.id as string,
      src: props.src as string,
      direct
    },
    props.thumb
  )
})

const shouldUseNuxtPicture = computed(
  () => !blobUrl.value && isStandardImageFormat.value
)

// 🚀 Bundle attributes, classes, and events to apply them to the inner <img> element
const imageAttributes = computed(() => ({
  ...attrs,
  class: [attrs.class, { lazyloading: !isImageReady.value }],
  loading: 'lazy',
  onLoad: handleLoad // Converts the Vue @load event to object syntax
}))
</script>

<template>
  <NuxtPicture v-if="shouldUseNuxtPicture && finalSrc" :src="finalSrc" provider="pocketbase" :alt="alt"
    :img-attrs="imageAttributes" :fetch-priority="fetchPriority" />

  <img v-else-if="finalSrc" :src="finalSrc" :alt="alt" loading="lazy" v-bind="$attrs"
    :class="{ lazyloading: !isImageReady }" @load="handleLoad">
</template>
